"use client";

import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "next-i18next";
import { motion } from "framer-motion";
import {
  Grid,
  TextField,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { enqueueSnackbar } from "notistack";
import {
  Phone,
  Email,
  LocationOn,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import GeneralService from "@/core/services/GeneralService";
import { SupportRequest } from "@/core/models/General.model";
import { parseJsonBody } from "@/lib/general.utils";
import useGeneralStore from "@/store/General/useGeneralStore";
import { PhoneCodeSelector } from "@/components/General/PhoneCodeSelector";

const ContactComponent = () => {
  const { t, i18n } = useTranslation("common");
  const isRtl = i18n.language === "ar";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneCode, setPhoneCode] = useState("+20");

  const { ContactComponent: contactData } = useGeneralStore((state) => ({
    ContactComponent: state.ContactComponent,
  }));
  const contactJson = parseJsonBody(contactData?.jsonBody);

  // Get data from database or fallback to defaults
  const contactInfo = contactJson?.contactInfo;
  const header = contactJson?.header;
  const galleryImages = contactJson?.galleryImages || [];
  const subjectOptions = contactJson?.subjectOptions || [];

  const validationSchema = Yup.object({
    name: Yup.string()
      .required(t("contact.nameRequired"))
      .min(2, t("contact.nameInvalid")),
    email: Yup.string()
      .email(t("contact.emailInvalid"))
      .required(t("contact.emailRequired")),
    phone: Yup.string()
      .required(t("contact.phoneRequired"))
      .min(8, t("contact.phoneInvalid")),
    subject: Yup.string().required(t("contact.subjectRequired")),
    body: Yup.string().required(t("contact.noteRequired")),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      subject: "",
      body: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        const supportData: Omit<SupportRequest, "id" | "insertDate"> = {
          name: values.name,
          email: values.email,
          phone: `${phoneCode}${values.phone}`,
          subject: values.subject,
          body: values.body,
        };

        const response = await GeneralService.AddSupport("Contact", {
          ...supportData,
          id: 0,
          insertDate: "",
        } as SupportRequest);

        if (response?.success) {
          enqueueSnackbar(
            t("contact.success") || "Message sent successfully!",
            {
              variant: "success",
            }
          );
          formik.resetForm();
        } else {
          throw new Error(response?.message || "Failed to send message");
        }
      } catch (error: any) {
        console.error("Error submitting contact form:", error);
        enqueueSnackbar(
          error?.message ||
            t("contact.error") ||
            "Failed to send message. Please try again.",
          {
            variant: "error",
          }
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <div>
      {/* Header Section - Contact Information */}
      <div className="bg-white py-[2rem] px-[2rem] border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-[2rem]">
            {/* Phone */}
            <div
              className="flex items-center w-full md:w-auto gap-[0.75rem]"
              dir="rtl"
            >
              <Phone className="text-primary text-[1.5rem]" />
              <div className="flex flex-col">
                <span className="text-[0.875rem] text-gray-600">
                  {contactInfo?.phoneLabel}
                </span>
                <span className="text-[1rem] font-medium text-black">
                  {contactInfo?.phone}
                </span>
              </div>
            </div>

            {/* Email */}
            <div
              className="flex items-center w-full md:w-auto gap-[0.75rem]"
              dir="rtl"
            >
              <Email className="text-primary text-[1.5rem]" />
              <div className="flex flex-col">
                <span className="text-[0.875rem] text-gray-600">
                  {contactInfo?.emailLabel}
                </span>
                <span className="text-[1rem] font-medium text-black">
                  {contactInfo?.email}
                </span>
              </div>
            </div>

            {/* Address */}
            <div
              className="flex items-center w-full md:w-auto gap-[0.75rem]"
              dir="rtl"
            >
              <LocationOn className="text-primary text-[1.5rem]" />
              <div className="flex flex-col">
                <span className="text-[1rem] font-medium text-black max-w-[20rem]">
                  {contactInfo?.address}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Form Section */}
      <section className="bg-white py-[1rem] md:py-[2rem] px-[2rem]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-[3rem]"
            dir="rtl"
          >
            <h1 className="text-[2rem] md:text-[2.5rem] font-bold text-primary mb-[1rem]">
              {header?.title}
            </h1>
            <p className="text-[1rem] md:text-[1.2rem] text-gray-700 leading-relaxed">
              {header?.subtitle}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <form onSubmit={formik.handleSubmit} className="w-full" dir="rtl">
              <Grid container>
                {/* Full Name Field */}
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    required
                    error={formik.touched.name && Boolean(formik.errors.name)}
                  >
                    <TextField
                      id="name"
                      name="name"
                      label={t("contact.namePlaceholder")}
                      placeholder={t("contact.namePlaceholder")}
                      size="small"
                      required
                      InputLabelProps={{ shrink: true }}
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.name && Boolean(formik.errors.name)}
                      helperText={
                        formik.touched.name && (formik.errors.name as string)
                      }
                      className="rounded-lg"
                      inputProps={{ dir: isRtl ? "rtl" : "ltr" }}
                    />
                  </FormControl>
                </Grid>

                {/* Phone Field */}
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    required
                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                  >
                    <div className="flex gap-2">
                      <div className="w-[8rem]">
                        <PhoneCodeSelector
                          value={phoneCode}
                          onChange={setPhoneCode}
                        />
                      </div>
                      <div className="flex-1">
                        <TextField
                          id="phone"
                          name="phone"
                          label={t("contact.phonePlaceholder")}
                          placeholder={t("contact.phonePlaceholder")}
                          type="tel"
                          size="small"
                          required
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={formik.values.phone}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.phone && Boolean(formik.errors.phone)
                          }
                          helperText={
                            formik.touched.phone &&
                            (formik.errors.phone as string)
                          }
                          className="rounded-lg"
                          inputProps={{ dir: "ltr" }}
                        />
                      </div>
                    </div>
                  </FormControl>
                </Grid>

                {/* Email Field */}
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    required
                    error={formik.touched.email && Boolean(formik.errors.email)}
                  >
                    <TextField
                      id="email"
                      name="email"
                      label={t("contact.emailPlaceholder")}
                      placeholder={t("contact.emailPlaceholder")}
                      type="email"
                      size="small"
                      required
                      InputLabelProps={{ shrink: true }}
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.email && Boolean(formik.errors.email)
                      }
                      helperText={
                        formik.touched.email && (formik.errors.email as string)
                      }
                      className="rounded-lg"
                      inputProps={{ dir: "ltr" }}
                    />
                  </FormControl>
                </Grid>

                {/* Subject Dropdown */}
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    required
                    error={
                      formik.touched.subject && Boolean(formik.errors.subject)
                    }
                  >
                    <Select
                      notched
                      id="subject-label"
                      name="subject"
                      labelId="subject-label"
                      value={formik.values.subject}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.subject && Boolean(formik.errors.subject)
                      }
                      size="small"
                      className="rounded-lg"
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        <em>{t("contact.subjectPlaceholder")}</em>
                      </MenuItem>
                      {subjectOptions.map((option: any) => (
                        <MenuItem key={option.id} value={option.label}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.subject && formik.errors.subject && (
                      <div className="text-[#e57d7a] text-sm mt-1">
                        {formik.errors.subject as string}
                      </div>
                    )}
                  </FormControl>
                </Grid>

                {/* Message Field (Textarea) */}
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    required
                    error={formik.touched.body && Boolean(formik.errors.body)}
                  >
                    <TextField
                      id="body"
                      name="body"
                      label={t("contact.notePlaceholder")}
                      placeholder={t("contact.notePlaceholder")}
                      multiline
                      rows={4}
                      size="small"
                      required
                      InputLabelProps={{ shrink: true }}
                      value={formik.values.body}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.body && Boolean(formik.errors.body)}
                      helperText={
                        formik.touched.body && (formik.errors.body as string)
                      }
                      className="rounded-lg"
                      inputProps={{ dir: isRtl ? "rtl" : "ltr" }}
                    />
                  </FormControl>
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <LoadingButton
                    type="submit"
                    fullWidth
                    variant="contained"
                    loading={isSubmitting}
                    className="rounded-lg bg-secondary hover:bg-secondary/80 py-[1rem] text-[1rem] font-semibold text-white normal-case"
                  >
                    {t("contact.submit")}
                  </LoadingButton>
                </Grid>
              </Grid>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Image Gallery Section */}
      {galleryImages.length > 0 && (
        <section className="bg-white py-[1rem] md:py-[1.5rem] px-[1rem] md:px-[2rem] w-full overflow-hidden">
          <div className="mx-auto max-w-[120rem] w-full">
            <div className="relative px-[0.5rem] md:px-[0.5rem]">
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                navigation={{
                  nextEl: ".contact-gallery-button-next",
                  prevEl: ".contact-gallery-button-prev",
                }}
                pagination={{
                  clickable: true,
                  dynamicBullets: true,
                }}
                autoplay={{
                  delay: 3000,
                  disableOnInteraction: false,
                }}
                breakpoints={{
                  640: {
                    slidesPerView: 2,
                    spaceBetween: 20,
                  },
                  768: {
                    slidesPerView: 3,
                    spaceBetween: 30,
                  },
                  1024: {
                    slidesPerView: 4,
                    spaceBetween: 30,
                  },
                }}
                loop={galleryImages.length > 4}
                className="pb-[4rem] contact-gallery-swiper"
              >
                {galleryImages.map((image: any, index: number) => (
                  <SwiperSlide key={index}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="relative aspect-[5/3] rounded-md overflow-hidden"
                    >
                      <Image
                        src={image.url || image || ""}
                        alt={`Gallery image ${index + 1}`}
                        fill
                        className="object-cover"
                      />
                    </motion.div>
                  </SwiperSlide>
                ))}
              </Swiper>

              {/* Custom Navigation */}
              <button className="contact-gallery-button-prev absolute left-0 md:left-[-1rem] top-1/2 -translate-y-1/2 z-20 w-[2.5rem] h-[2.5rem] md:w-[3rem] md:h-[3rem] flex items-center justify-center bg-primary hover:bg-[#0a4384] rounded-full transition-colors shadow-lg">
                <ChevronLeft className="text-white text-[1.5rem] md:text-[2rem]" />
              </button>
              <button className="contact-gallery-button-next absolute right-0 md:right-[-1rem] top-1/2 -translate-y-1/2 z-20 w-[2.5rem] h-[2.5rem] md:w-[3rem] md:h-[3rem] flex items-center justify-center bg-primary hover:bg-[#0a4384] rounded-full transition-colors shadow-lg">
                <ChevronRight className="text-white text-[1.5rem] md:text-[2rem]" />
              </button>
            </div>

            {/* Custom Pagination Styles */}
            <style jsx global>{`
              .contact-gallery-swiper .swiper-pagination {
                bottom: 1rem !important;
              }
              .contact-gallery-swiper .swiper-pagination-bullet {
                width: 0.75rem !important;
                height: 0.75rem !important;
                background: rgba(26, 43, 107, 0.3) !important;
                opacity: 1 !important;
                margin: 0 0.25rem !important;
              }
              .contact-gallery-swiper .swiper-pagination-bullet-active {
                background: #1a2b6b !important;
                width: 1.5rem !important;
                border-radius: 0.375rem !important;
              }
            `}</style>
          </div>
        </section>
      )}
    </div>
  );
};

export default ContactComponent;
